// SB6-ANA-FILT-06 (Track D / D9) — save/load presets control.
//
// Rendered into the `FacetTableWidget` header slot by both advanced
// widgets. It is a thin, presentational control wired to the
// `useFilterConfigs` hooks via props from the parent widget (which owns
// the `(kind, modelSignature)` scope + the live config blob):
//
//   * a preset dropdown listing the user's NAMED presets for the current
//     `(kind, modelSignature)` (the implicit last-used row is filtered out
//     upstream — it never appears as a selectable preset);
//   * "Save as…" → a small dialog prompting for a name → create (POST);
//   * "Update" → overwrite the currently-loaded preset's blob (PATCH);
//   * "Delete" → a confirm dialog → delete (DELETE).
//
// Loading a preset is the parent's job (`onLoad(config)`): the parent
// applies the blob to the widget state (checked classes + per-class
// minConfidencePct for AI; checked defect types for Defect), which then
// emits the usual aiClassFilter / defectTypeIds. Presets are only listable
// when their `modelSignature` matches the current DA's, so a config cannot
// be loaded across mismatched setups (D9) — the list query already filters
// by the signature.
import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import type { FilterConfig } from "@aeriosiq/types";
import { SaveIcon, Trash2Icon } from "@aeriosiq/ui/icons";

export interface FilterConfigPresetsProps {
  /** `data-testid` root (e.g. `ai-filter-presets`). */
  testId: string;
  /** The user's NAMED presets for the current (kind, modelSignature). */
  presets: readonly FilterConfig[];
  /** The currently-loaded preset id, or `""` when none is loaded. */
  selectedId: string;
  /** Whether the presets control is usable (false until the signature settles). */
  disabled?: boolean;
  /** A save/update/delete request is in flight. */
  busy?: boolean;
  /** User picked a preset from the dropdown (id `""` ⇒ cleared selection). */
  onSelect: (configId: string) => void;
  /** User confirmed "Save as…" with a (trimmed, non-empty) name. */
  onSaveAs: (name: string) => void;
  /** User clicked "Update" to overwrite the loaded preset's blob. */
  onUpdate: (configId: string) => void;
  /** User confirmed deleting a preset. */
  onDelete: (configId: string) => void;
}

/**
 * Presets dropdown + save/update/delete actions for an advanced filter
 * widget. See the file header for the wiring contract.
 */
export function FilterConfigPresets({
  testId,
  presets,
  selectedId,
  disabled = false,
  busy = false,
  onSelect,
  onSaveAs,
  onUpdate,
  onDelete,
}: FilterConfigPresetsProps): React.ReactElement {
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const selected = presets.find((p) => p.id === selectedId) ?? null;
  const trimmed = name.trim();

  const closeSave = (): void => {
    setSaveOpen(false);
    setName("");
  };

  const confirmSave = (): void => {
    if (trimmed.length === 0) return;
    onSaveAs(trimmed);
    closeSave();
  };

  return (
    <Box
      data-testid={testId}
      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
    >
      <FormControl size="small" sx={{ minWidth: 140 }} disabled={disabled}>
        <InputLabel id={`${testId}-label`}>Preset</InputLabel>
        <Select
          labelId={`${testId}-label`}
          label="Preset"
          value={selectedId}
          displayEmpty
          onChange={(e) => {
            onSelect(e.target.value);
          }}
          inputProps={{
            "aria-label": "Saved filter presets",
            "data-testid": `${testId}-select`,
          }}
        >
          <MenuItem value="" data-testid={`${testId}-option-none`}>
            <em>None</em>
          </MenuItem>
          {presets.map((p) => (
            <MenuItem
              key={p.id}
              value={p.id}
              data-testid={`${testId}-option-${p.id}`}
            >
              {p.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Tooltip title="Save as new preset">
        <span>
          <IconButton
            size="small"
            disabled={disabled || busy}
            onClick={() => {
              setSaveOpen(true);
            }}
            aria-label="Save as new preset"
            data-testid={`${testId}-save-as`}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      {selected ? (
        <>
          <Tooltip title={`Update “${selected.name ?? ""}”`}>
            <span>
              <Button
                size="small"
                variant="text"
                disabled={disabled || busy}
                onClick={() => {
                  onUpdate(selected.id);
                }}
                data-testid={`${testId}-update`}
              >
                Update
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={`Delete “${selected.name ?? ""}”`}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={disabled || busy}
                onClick={() => {
                  setConfirmDeleteId(selected.id);
                }}
                aria-label="Delete preset"
                data-testid={`${testId}-delete`}
              >
                <Trash2Icon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </>
      ) : null}

      {/* Save-as dialog (a name prompt; nicer than window.prompt for a11y). */}
      <Dialog
        open={saveOpen}
        onClose={closeSave}
        data-testid={`${testId}-save-dialog`}
      >
        <DialogTitle>Save filter preset</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Preset name"
            fullWidth
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmSave();
            }}
            slotProps={{
              htmlInput: {
                maxLength: 200,
                "data-testid": `${testId}-name-input`,
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSave} data-testid={`${testId}-save-cancel`}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={trimmed.length === 0 || busy}
            onClick={confirmSave}
            data-testid={`${testId}-save-confirm`}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm. */}
      <Dialog
        open={confirmDeleteId !== null}
        onClose={() => {
          setConfirmDeleteId(null);
        }}
        data-testid={`${testId}-delete-dialog`}
      >
        <DialogTitle>Delete preset</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently removes the saved preset. Your current filter
            selection is unaffected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDeleteId(null);
            }}
            data-testid={`${testId}-delete-cancel`}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={() => {
              if (confirmDeleteId) onDelete(confirmDeleteId);
              setConfirmDeleteId(null);
            }}
            data-testid={`${testId}-delete-confirm`}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
